import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: Skip /me if returning from OAuth callback; AuthCallback processes session_id first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed; clearing local session anyway", err);
    }
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, setUser, loading, refresh, logout }), [user, loading, refresh, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
    else if (!loading && user && !user.approved) navigate("/pending", { replace: true });
  }, [loading, user, navigate]);
  if (loading || !user || !user.approved) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-500 font-mono">Verifying session…</div>
      </div>
    );
  }
  return children;
}

export function AdminRoute({ children }) {
  const { user } = useAuth();
  return <ProtectedRoute>{user && user.approved && user.role !== "admin" ? <Navigate to="/today" replace /> : children}</ProtectedRoute>;
}

export function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.approved) return <Navigate to="/pending" replace />;
  return <Navigate to={user.role === "admin" ? "/dashboard" : "/today"} replace />;
}
